import { useState, useCallback } from 'react';
import './ToolStyles.css';

/* ============================================================
 * 分词工具 — 将输入文本拆分为单词数组，识别多种分隔符
 * ============================================================ */

/**
 * 按显式分隔符（空白、-、_）和执行大小写边界拆分，将输入文本分解为单词数组。
 * 处理示例：
 *   "hello world"      → ["hello", "world"]
 *   "camelCase"        → ["camel", "Case"]
 *   "HTMLEditor"       → ["HTML", "Editor"]
 *   "hello-world_test" → ["hello", "world", "test"]
 */
const splitWords = (input: string): string[] => {
  if (!input.trim()) return [];

  // 第一步：按显式分隔符（空白、-、_）拆分，保留各段
  const segments = input.split(/[\s\-_]+/).filter(Boolean);
  if (segments.length === 0) return [];

  // 第二步：对每段按大小写边界拆分
  const words: string[] = [];
  for (const seg of segments) {
    if (!seg) continue;

    // 无字母的纯符号/数字段，原样保留
    if (!/[a-zA-Z]/.test(seg)) {
      words.push(seg);
      continue;
    }

    let i = 0;
    const len = seg.length;
    while (i < len) {
      // 跳过连续的非字母字符作为分隔
      while (i < len && !/[a-zA-Z]/.test(seg[i])) {
        i++;
      }
      if (i >= len) break;

      const start = i;

      // 首字符是大写
      if (seg[i] === seg[i].toUpperCase() && seg[i] !== seg[i].toLowerCase()) {
        i++;
        // 收集连续大写字母（可能组成缩写如 HTML、XML）
        while (i < len && seg[i] === seg[i].toUpperCase() && seg[i] !== seg[i].toLowerCase()) {
          i++;
        }
        // 如果后面跟着小写字母，说明最后一个大写属于下一个单词
        // 例: "HTMLEditor" → 连续大写到 'L'，后面是 'd'（小写），
        //     回退最后一个大写归入下一单词 → "HTML" + "Editor"
        if (i < len && seg[i] === seg[i].toLowerCase() && seg[i] !== seg[i].toUpperCase()) {
          if (i - start > 1) {
            // 有 ≥2 个大写，回退最后一个给下一个单词
            i--;
          }
        }
        words.push(seg.slice(start, i));
      } else {
        // 首字符是小写
        i++;
        while (i < len && (seg[i] === seg[i].toLowerCase() || seg[i] === seg[i].toUpperCase()) && seg[i] === seg[i].toLowerCase()) {
          i++;
        }
        words.push(seg.slice(start, i));
      }
    }
  }

  return words.length > 0 ? words : segments;
};

/* ============================================================
 * 10 种英文大小写转换函数
 * ============================================================ */

/** 1. UPPERCASE — 全部字母转大写，原样保留分隔符 */
const toUpperCase = (input: string): string => input.toUpperCase();

/** 2. lowercase — 全部字母转小写，原样保留分隔符 */
const toLowerCase = (input: string): string => input.toLowerCase();

/** 3. Title Case — 每个单词首字母大写，原样保留分隔符 */
const toTitleCase = (input: string): string => {
  return input.replace(/[a-zA-Z]+/g, (match) => {
    return match.charAt(0).toUpperCase() + match.slice(1).toLowerCase();
  });
};

/** 4. Sentence case — 仅句首字母大写 */
const toSentenceCase = (input: string): string => {
  if (!input.trim()) return '';
  // 找到第一个字母并大写，其余全部小写
  const firstLetterMatch = input.match(/[a-zA-Z]/);
  if (!firstLetterMatch) return input;
  const idx = firstLetterMatch.index!;
  return (
    input.slice(0, idx) +
    input[idx].toUpperCase() +
    input.slice(idx + 1).toLowerCase()
  );
};

/** 5. camelCase — 第一个单词小写，后续单词首字母大写，无分隔符 */
const toCamelCase = (input: string): string => {
  const words = splitWords(input);
  if (words.length === 0) return '';
  return words
    .map((w, i) =>
      i === 0
        ? w.toLowerCase()
        : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
    )
    .join('');
};

/** 6. PascalCase — 每个单词首字母大写，无分隔符 */
const toPascalCase = (input: string): string => {
  const words = splitWords(input);
  if (words.length === 0) return '';
  return words
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join('');
};

/** 7. snake_case — 下划线分隔，全小写 */
const toSnakeCase = (input: string): string => {
  const words = splitWords(input);
  if (words.length === 0) return '';
  return words.map((w) => w.toLowerCase()).join('_');
};

/** 8. CONSTANT_CASE — 下划线分隔，全大写 */
const toConstantCase = (input: string): string => {
  const words = splitWords(input);
  if (words.length === 0) return '';
  return words.map((w) => w.toUpperCase()).join('_');
};

/** 9. kebab-case — 连字符分隔，全小写 */
const toKebabCase = (input: string): string => {
  const words = splitWords(input);
  if (words.length === 0) return '';
  return words.map((w) => w.toLowerCase()).join('-');
};

/** 10. Train-Case — 连字符分隔，每个单词首字母大写 */
const toTrainCase = (input: string): string => {
  const words = splitWords(input);
  if (words.length === 0) return '';
  return words
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join('-');
};

/* ============================================================
 * 转换模式定义
 * ============================================================ */

interface CaseMode {
  key: string;
  label: string;
  description: string;
  convert: (input: string) => string;
}

const caseModes: CaseMode[] = [
  { key: 'upper', label: 'UPPERCASE', description: '全部字母转大写', convert: toUpperCase },
  { key: 'lower', label: 'lowercase', description: '全部字母转小写', convert: toLowerCase },
  { key: 'title', label: 'Title Case', description: '每个单词首字母大写', convert: toTitleCase },
  { key: 'sentence', label: 'Sentence case', description: '仅句首字母大写', convert: toSentenceCase },
  { key: 'camel', label: 'camelCase', description: '驼峰命名（无分隔符）', convert: toCamelCase },
  { key: 'pascal', label: 'PascalCase', description: '帕斯卡命名（无分隔符）', convert: toPascalCase },
  { key: 'snake', label: 'snake_case', description: '下划线分隔，全小写', convert: toSnakeCase },
  { key: 'constant', label: 'CONSTANT_CASE', description: '下划线分隔，全大写', convert: toConstantCase },
  { key: 'kebab', label: 'kebab-case', description: '连字符分隔，全小写', convert: toKebabCase },
  { key: 'train', label: 'Train-Case', description: '连字符分隔，首字母大写', convert: toTrainCase },
];

/* ============================================================
 * 数字大小写转换（现有功能，保留不变）
 * ============================================================ */

const NumberTab = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');

  const arabicToChineseMap: { [key: string]: string } = {
    '0': '零', '1': '壹', '2': '贰', '3': '叁', '4': '肆',
    '5': '伍', '6': '陆', '7': '柒', '8': '捌', '9': '玖',
  };

  const chineseToArabicMap: { [key: string]: string } = {
    '零': '0', '一': '1', '二': '2', '三': '3', '四': '4',
    '五': '5', '六': '6', '七': '7', '八': '8', '九': '9',
    '壹': '1', '贰': '2', '叁': '3', '肆': '4', '伍': '5',
    '陆': '6', '柒': '7', '捌': '8', '玖': '9',
  };

  const units = ['', '拾', '佰', '仟'];
  const bigUnits = ['', '万', '亿', '兆'];

  const convertToChineseCapital = (num: string): string => {
    if (num === '0') return '零';

    const parts = num.split('.');
    const intPart = parts[0];
    let result = '';

    // 处理整数部分
    const len = intPart.length;
    const groups = Math.ceil(len / 4);

    for (let i = 0; i < groups; i++) {
      const start = Math.max(0, len - (i + 1) * 4);
      const end = len - i * 4;
      const group = intPart.slice(start, end);

      let groupResult = '';
      for (let j = 0; j < group.length; j++) {
        const digit = group[j];
        if (digit !== '0') {
          groupResult += arabicToChineseMap[digit] + units[group.length - 1 - j];
        } else if (groupResult && groupResult[groupResult.length - 1] !== '零') {
          groupResult += '零';
        }
      }

      if (groupResult) {
        // 移除末尾的零
        groupResult = groupResult.replace(/零+$/, '');
        result = groupResult + bigUnits[i] + result;
      }
    }

    // 处理小数部分
    if (parts[1]) {
      result += '点';
      for (const digit of parts[1]) {
        result += arabicToChineseMap[digit];
      }
    }

    return result || '零';
  };

  const handleConvert = (value: string) => {
    setInput(value);
    setError('');

    if (!value.trim()) {
      setOutput('');
      return;
    }

    try {
      // 检测是否为阿拉伯数字
      if (/^[0-9.]+$/.test(value)) {
        const result = convertToChineseCapital(value);
        setOutput(result);
      }
      // 检测是否为中文数字
      else if (/^[零一二三四五六七八九壹贰叁肆伍陆柒捌玖点.]+$/.test(value)) {
        let arabicNum = '';
        for (const char of value) {
          if (char === '点' || char === '.') {
            arabicNum += '.';
          } else if (chineseToArabicMap[char]) {
            arabicNum += chineseToArabicMap[char];
          }
        }
        setOutput(convertToChineseCapital(arabicNum));
      } else {
        setError('请输入有效的数字或中文数字');
        setOutput('');
      }
    } catch {
      setError('转换失败，请检查输入');
      setOutput('');
    }
  };

  return (
    <>
      <div className="input-group">
        <label className="input-label">输入数字</label>
        <input
          type="text"
          className="tool-input"
          value={input}
          onChange={(e) => handleConvert(e.target.value)}
          placeholder="输入阿拉伯数字或中文数字"
        />
        {error && <div className="error-message">{error}</div>}
      </div>

      <div className="output-group">
        <label className="output-label">中文大写</label>
        <div className="tool-output">{output || '等待输入...'}</div>
      </div>

      <div className="info-box">
        <h4>使用说明：</h4>
        <ul>
          <li>支持阿拉伯数字转中文大写</li>
          <li>支持中文小写转中文大写</li>
          <li>支持小数点转换</li>
          <li>示例：123 → 壹佰贰拾叁</li>
        </ul>
      </div>
    </>
  );
};

/* ============================================================
 * 英文大小写转换 Tab
 * ============================================================ */

const EnglishTab = () => {
  const [input, setInput] = useState('');
  const [activeMode, setActiveMode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const result = activeMode
    ? caseModes.find((m) => m.key === activeMode)!.convert(input)
    : '';

  const handleCopy = useCallback(async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // 降级方案
      const ta = document.createElement('textarea');
      ta.value = result;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [result]);

  return (
    <>
      <div className="input-group">
        <label className="input-label">输入文本</label>
        <textarea
          className="tool-textarea"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setCopied(false);
          }}
          placeholder="输入英文文本，支持多行..."
          rows={4}
          style={{ minHeight: 100 }}
        />
      </div>

      <div className="output-group">
        <div className="output-header">
          <label className="output-label">转换模式（点击按钮直接转换）</label>
        </div>
        <div className="case-button-grid">
          {caseModes.map((mode) => (
            <button
              key={mode.key}
              className={`case-button${activeMode === mode.key ? ' active' : ''}`}
              onClick={() => setActiveMode(mode.key)}
              title={mode.description}
            >
              {mode.label}
            </button>
          ))}
        </div>
      </div>

      <div className="output-group">
        <div className="output-header">
          <label className="output-label">
            转换结果
            {activeMode && (
              <span className="mode-hint">
                — {caseModes.find((m) => m.key === activeMode)!.description}
              </span>
            )}
          </label>
          <button
            className="copy-button"
            onClick={handleCopy}
            disabled={!result}
          >
            {copied ? '✓ 已复制' : '📋 复制'}
          </button>
        </div>
        <div className={`tool-output code${!result ? ' placeholder' : ''}`}>
          {result || '请先输入文本并选择转换模式...'}
        </div>
      </div>
    </>
  );
};

/* ============================================================
 * 主组件 — Tab 切换
 * ============================================================ */

const NumberConverter = () => {
  const [activeTab, setActiveTab] = useState<'number' | 'english'>('number');

  return (
    <div className="tool-container">
      {/* Tab 切换按钮 */}
      <div className="tab-bar">
        <button
          className={`tab-button${activeTab === 'number' ? ' active' : ''}`}
          onClick={() => setActiveTab('number')}
        >
          数字大写
        </button>
        <button
          className={`tab-button${activeTab === 'english' ? ' active' : ''}`}
          onClick={() => setActiveTab('english')}
        >
          英文大小写
        </button>
      </div>

      {activeTab === 'number' ? <NumberTab /> : <EnglishTab />}
    </div>
  );
};

export default NumberConverter;
